/**
 * Smart Query Matcher
 * Matches user queries to pre-defined responses with fuzzy matching
 */

import { RESPONSES, QUERY_PATTERNS, Response } from '../data/responses';

export type QueryType = 'selective' | 'comparative' | 'recommendation' | 'version';

export interface MatchResult {
  response: Response;
  matchScore: number;
  queryType: QueryType;
}

/**
 * Classify query type based on patterns
 */
export const classifyQuery = (query: string): QueryType => {
  const lowerQuery = query.toLowerCase();

  // Check version patterns
  for (const pattern of QUERY_PATTERNS.version) {
    if (pattern.test(query)) {
      return 'version';
    }
  }

  // Check comparative patterns
  for (const pattern of QUERY_PATTERNS.comparative) {
    if (pattern.test(query)) {
      return 'comparative';
    }
  }

  // Check recommendation patterns
  for (const pattern of QUERY_PATTERNS.recommendation) {
    if (pattern.test(query)) {
      return 'recommendation';
    }
  }

  // Check selective patterns
  for (const pattern of QUERY_PATTERNS.selective) {
    if (pattern.test(query)) {
      return 'selective';
    }
  }

  return 'selective';
};

/**
 * Calculate similarity score between two strings
 */
const calculateSimilarity = (str1: string, str2: string): number => {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();

  // Exact match
  if (s1 === s2) return 1.0;

  // Contains match
  if (s1.includes(s2) || s2.includes(s1)) {
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length <= s2.length ? s1 : s2;
    return shorter.length / longer.length;
  }

  // Word overlap
  const words1 = s1.split(/\s+/);
  const words2 = s2.split(/\s+/);
  const commonWords = words1.filter(w => words2.includes(w)).length;
  const totalWords = Math.max(words1.length, words2.length);

  return commonWords / totalWords * 0.8; // Lower score for word overlap
};

/**
 * Find best matching response for a query
 */
export const findBestMatch = (query: string): MatchResult => {
  let bestMatch: MatchResult | null = null;
  let highestScore = 0;

  // Try to find exact or fuzzy match in RESPONSES
  for (const [knownQuery, response] of Object.entries(RESPONSES)) {
    const score = calculateSimilarity(query, knownQuery);

    if (score > highestScore) {
      highestScore = score;
      bestMatch = {
        response,
        matchScore: score,
        queryType: response.type as QueryType
      };
    }
  }

  // If we found a good match (>60% similarity), return it
  if (bestMatch && highestScore > 0.6) {
    return bestMatch;
  }

  // Otherwise, generate a contextual response
  const queryType = classifyQuery(query);
  return {
    response: generateFallbackResponse(query, queryType),
    matchScore: 0.5,
    queryType
  };
};

/**
 * Generate fallback response when no good match is found
 */
const generateFallbackResponse = (query: string, queryType: QueryType): Response => {
  const queryLower = query.toLowerCase();

  // Extract potential product names from query
  const products = ['신한', 'KB', 'AIA', '메리츠', '프로미', '한화', '교보'];
  const mentionedProducts = products.filter(p => queryLower.includes(p.toLowerCase()));

  switch (queryType) {
    case 'version':
      return {
        type: 'version',
        text: `**버전 비교 요청**을 확인했습니다.

현재 시스템에 해당 상품의 상세 버전 정보가 로드되어 있습니다.

**💡 추천 질문:**
• "신한통합건강보장보험 ONE의 2025년 1월 버전과 2월 버전의 차이는?"
• "KB플러스운전자상해보험의 2025년 8월과 9월 버전 차이를 알려주세요"

왼쪽의 **Version Comparison** 패널에서 상품을 선택하시면 자동으로 버전 비교가 가능합니다.`,
        sources: []
      };

    case 'comparative':
      if (mentionedProducts.length >= 2) {
        return {
          type: 'comparative',
          text: `**${mentionedProducts.join('와 ')} 상품 비교** 요청을 받았습니다.

시스템이 다음 항목을 분석 중입니다:
• 보장 금액 및 범위
• 보험료 (연령대별)
• 특약 및 부가 서비스
• 대기 기간 및 조건

**💡 더 정확한 비교를 위해:**
구체적인 상품명을 전체 입력해주시면 상세한 비교표를 제공해드립니다.

**예시 질문:**
• "30세 직장인에게 KB플러스와 메리츠 운전자보험 중 어떤 것을 추천하시나요?"
• "신한통합건강과 AIAVitality의 암 보장을 비교해주세요"`,
          sources: []
        };
      }
      return {
        type: 'comparative',
        text: `**비교 분석** 요청을 확인했습니다.

현재 ${RESPONSES && Object.keys(RESPONSES).length > 0 ? '15개' : '여러'} 보험 상품의 상세 정보가 시스템에 로드되어 있습니다.

**비교 가능한 항목:**
• 보험료 (연령별)
• 보장 금액
• 대기 기간
• 특약 및 부가서비스
• 가입 조건

**💡 질문 예시:**
• "30세에게 적합한 건강보험 비교"
• "운전자보험 중 가장 경제적인 상품은?"`,
          sources: []
        };

    case 'recommendation':
      return {
        type: 'recommendation',
        text: `**맞춤 추천** 요청을 확인했습니다.

더 정확한 추천을 위해 다음 정보를 포함해주세요:
• 연령대 (예: 30세, 40대 등)
• 관심 보험 종류 (건강/운전자/종신)
• 예산 범위 (선택사항)
• 중요하게 생각하는 보장 (예: 암, 사고 등)

**💡 질문 예시:**
• "30세 고객에게 암보험 추천해주세요"
• "40대 직장인에게 적합한 운전자보험은?"
• "저렴한 보험료의 건강보험 추천"`,
        sources: []
      };

    case 'selective':
    default:
      // Try to provide helpful guidance
      if (queryLower.includes('암') && queryLower.includes('진단금')) {
        return {
          type: 'selective',
          text: `**암 진단금** 관련 질문을 확인했습니다.

시스템에 등록된 건강보험 상품들의 암 진단금 정보:

• **AIAVitality 1형**: 5,000만원 (최고 보장)
• **AIAVitality 2형**: 4,000만원
• **신한통합건강 ONE**: 3,000만원
• **메리츠 간편건강보험**: 2,000만원
• **letsimple 건강보험**: 1,000만원

각 상품의 대기기간은 모두 90일입니다.

**💡 더 자세한 정보가 필요하시면:**
특정 상품명을 포함하여 질문해주세요.`,
          sources: []
        };
      }

      if (queryLower.includes('보험료') || queryLower.includes('얼마')) {
        return {
          type: 'selective',
          text: `**보험료** 관련 질문을 확인했습니다.

보험료는 다음 요소에 따라 달라집니다:
• 연령 (30세 기준으로 안내 가능)
• 성별
• 보장 범위
• 특약 선택

**💡 정확한 보험료 확인:**
"[상품명]의 보험료는 얼마인가요?" 형식으로 질문해주세요.

**예시:**
• "신한통합건강보장보험 ONE의 30세 보험료는?"
• "KB플러스운전자상해보험 가격 알려주세요"`,
          sources: []
        };
      }

      return {
        type: 'selective',
        text: `질문을 확인했습니다.

현재 시스템에는 **15개 보험 상품**, **18개 버전**, **54개 문서**가 로드되어 있습니다.

**💬 질문 도우미:**

**🔍 상세 조회 (Selective)**
• "[상품명]의 암 진단금 대기기간은?"
• "[상품명]의 보험료는 얼마인가요?"

**⚖️ 상품 비교 (Comparative)**
• "[상품A]와 [상품B]를 비교해주세요"
• "운전자보험 중 어떤 게 좋나요?"

**💡 추천 (Recommendation)**
• "30세 고객에게 암보험 추천해주세요"
• "경제적인 건강보험 추천"

**🔄 버전 비교 (Version)**
• "[상품명]의 2025년 1월과 2월 버전 차이는?"

왼쪽 패널에서 샘플 질문을 선택하거나, Product Explorer에서 상품을 클릭해보세요!`,
        sources: []
      };
  }
};

/**
 * Simulate processing delay for realistic feel
 */
export const simulateProcessing = async (query: string): Promise<number> => {
  // Longer queries take a bit more time
  const baseTime = 800;
  const queryComplexity = Math.min(query.length / 50, 1) * 400;
  const randomVariation = Math.random() * 200;

  const totalTime = baseTime + queryComplexity + randomVariation;

  return totalTime;
};
